import { gql } from "@apollo/client";
import { addDays, differenceInCalendarDays, format, isSameDay, startOfDay } from "date-fns";

// Keys match the fields returned by projectActivityStats / the metric arg of projectActivityDetails
export type MetricKey = "outboundCalls" | "inboundCalls" | "eblast" | "sales" | "scheduled" | "showedUp" | "toSales" | "emailsSent" | "notes";

export interface IMetric {
  key: MetricKey;
  label: string;
  short: string;
  // Column headers for the details modal
  dateLabel: string;
  userLabel?: string;
  detailLabel?: string;
  // Appointment metrics show the appointment's status flags instead of a detail column.
  // "created" also shows the appointment date, since the date column is when it was created
  appointment?: "created" | "scheduled";
  // Call metrics show the Twilio call's numbers, duration and status
  call?: boolean;
}

export const METRICS: IMetric[] = [
  { key: "outboundCalls", label: "Outbound Calls", short: "Outbound Calls", dateLabel: "Date", userLabel: "Caller", call: true },
  { key: "inboundCalls", label: "Inbound Calls", short: "Inbound Calls", dateLabel: "Date", call: true },
  { key: "eblast", label: "Appointments Created (eBlast)", short: "Appointments(eBlast)", dateLabel: "Created", appointment: "created" },
  {
    key: "sales",
    label: "Appointments Created (Sales)",
    short: "Appointments(Sales)",
    dateLabel: "Created",
    userLabel: "Created By",
    appointment: "created",
  },
  {
    key: "scheduled",
    label: "Appointments Scheduled",
    short: "Appointments Scheduled",
    dateLabel: "Appointment Date",
    userLabel: "Sales Rep",
    appointment: "scheduled",
  },
  {
    key: "showedUp",
    label: "Appointments Showed Up",
    short: "Appointments Showed Up",
    dateLabel: "Appointment Date",
    userLabel: "Sales Rep",
    appointment: "scheduled",
  },
  { key: "toSales", label: "Appointments To Sales", short: "Appointments To Sales", dateLabel: "Date", userLabel: "Sales Rep", detailLabel: "Event" },
  { key: "emailsSent", label: "Emails Sent", short: "Emails", dateLabel: "Sent", userLabel: "Sent By", detailLabel: "Subject" },
  { key: "notes", label: "Notes Created", short: "Notes", dateLabel: "Created", userLabel: "Created By", detailLabel: "Note" },
];

export type MetricTotals = Record<MetricKey, number>;

export interface IDayStats {
  date: Date;
  totals: MetricTotals;
  activeUsers: string[];
}

export interface IActivityDetail {
  day: string;
  date: Date;
  name: string;
  email: string;
  user: string;
  detail: string;
  appointment: {
    date: Date;
    location: string;
    status: string;
    confirmed: boolean;
    cameIn: boolean;
    cancelled: boolean;
    noResponse: boolean;
  } | null;
  call: {
    from: string;
    to: string;
    duration: number;
    status: string;
  } | null;
}

export const emptyTotals = (): MetricTotals => METRICS.reduce((acc, { key }) => ({ ...acc, [key]: 0 }), {} as MetricTotals);

// One entry per day in the range from projectActivityStats (missing days are all zero)
export const toDays = (stats: any[], dateStart: Date, dateEnd: Date): IDayStats[] => {
  const activity = new Map<string, any>((stats || []).map((day: any) => [day.date, day]));
  return Array.from({ length: differenceInCalendarDays(dateEnd, dateStart) + 1 }, (_, i) => addDays(startOfDay(dateStart), i)).map((date) => {
    const day = activity.get(format(date, "yyyy-MM-dd"));
    const totals: MetricTotals = emptyTotals();
    if (day) METRICS.forEach(({ key }) => (totals[key] = day[key] || 0));
    return { date, totals, activeUsers: day?.activeUsers || [] };
  });
};

export const formatRange = (dateStart: Date, dateEnd: Date) =>
  isSameDay(dateStart, dateEnd) ? format(dateStart, "MMM d, yyyy") : `${format(dateStart, "MMM d, yyyy")} - ${format(dateEnd, "MMM d, yyyy")}`;

export const sumTotals = (rows: MetricTotals[]): MetricTotals =>
  rows.reduce((acc, row) => {
    METRICS.forEach(({ key }) => (acc[key] += row[key] || 0));
    return acc;
  }, emptyTotals());

// Project wide counts per day (Toronto time). Calls are Twilio calls on the project's tracking phones. eBlast = appointment has no user and no salesRep, otherwise Sales.
// Emails are every project mail without a mailSchedule, notes are every comment on the project.
export const GETPROJECTACTIVITY = gql`
  query projectActivityStats($project: MongoID!, $dateStart: Date!, $dateEnd: Date!) {
    projectActivityStats(project: $project, dateStart: $dateStart, dateEnd: $dateEnd) {
      date
      outboundCalls
      inboundCalls
      eblast
      sales
      scheduled
      showedUp
      toSales
      emailsSent
      notes
      activeUsers
    }
  }
`;

export const GETPROJECTACTIVITYDETAILS = gql`
  query projectActivityDetails($project: MongoID!, $metric: String!, $dateStart: Date!, $dateEnd: Date!) {
    projectActivityDetails(project: $project, metric: $metric, dateStart: $dateStart, dateEnd: $dateEnd) {
      day
      date
      name
      email
      user
      detail
      appointment {
        date
        location
        status
        confirmed
        cameIn
        cancelled
        noResponse
      }
      call {
        from
        to
        duration
        status
      }
    }
  }
`;
